import React from 'react';

import Icon, {Icons} from '@pb/kit-component-icon';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import pageStyles from '../page.component.module.styl';

import styles from './page-logo.module.styl';

import type {ReactNode} from 'react';
import {FlatButton} from '@pb/kit-component-button';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);
const pageClasses = hashedClasses(pageStyles);

export type PageLogoProps = {
    /**
     * Custom logo element — typically an anchor wrapping a logo SVG or `<Icon>`.
     * When provided, it fully replaces the built-in PitchBook logo and `handler`
     * link behavior.
     */
    children?: ReactNode,
    /**
     * Node rendered to the right of the logo. Use for a secondary icon or badge
     * adjacent to the logo mark.
     * @deprecated It will be removed in 6 months 09-15-2025. provide children instead
     */
    anchorAfter?: ReactNode,
    /**
     * URL navigated to when the logo is clicked. Internally wraps the default
     * PitchBook logo in a `<FlatButton as="a">` with this `href`.
     * @deprecated It will be removed in 6 months 09-15-2025. provide children with link instead
     */
    handler?: string,
    /**
     * Click handler attached to the logo wrapper `<div>`. Only used when
     * `children` and `handler` are absent.
     * @deprecated It will be removed in 6 months 09-15-2025. provide children with link instead
     */
    onClick?: () => void,
    /**
     * Additional CSS class merged onto the logo wrapper `<div>`.
     */
    className?: string,
};

/**
 * Logo slot rendered inside a `<PageHeader>`.
 *
 * By default renders the PitchBook logo icon. Pass `children` to supply a fully
 * custom logo element (e.g. an `<a>` wrapping an SVG). The deprecated `handler`
 * and `onClick` props provide basic link/click behavior without custom children.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/story/layout-page--page-with-logo}
 */
export const PageLogo = (props: PageLogoProps) => {
    const {
        handler,
        anchorAfter,
        onClick,
        children,
        className,
    } = props;

    const pageLogoClasses = classNames('logo', {
        logo_pointer: Boolean(handler) || Boolean(onClick),
    });

    /**
     * Icon component for logo
     * @deprecated It will be removed in 6 months 09-15-2025. provide children component instead
     */
    const logo = (
        <Icon
            type={Icons.PITCHBOOK_LOGO_V2}
            block={true}
            className={classes.logo__icon}
        />
    );

    const getContent = () => {
        if (children) {
            return children;
        }
        if (handler) {
            return <FlatButton href={handler} as="a">{logo}</FlatButton>;
        }
        return logo;
    };

    const getClassNames = (): string => {
        return classNames(`${pageClasses.page__logo} ${pageLogoClasses}`, className);
    };

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div
            className={getClassNames()}
            onClick={onClick}>
            {getContent()}
            {anchorAfter}
        </div>
    );
};

PageLogo.displayName = 'PageLogo';
